import {
  faArrowRightToCity,
  faCakeCandles,
  faCalendarCheck,
  faPeopleGroup,
  faRadiation,
  faSignature
} from '@fortawesome/free-solid-svg-icons';
import type { JSX } from 'react';

import LastAppointmentBanner from '@/components/LastAppointmentBanner';
import { useRequestedFormCompletions } from '@/hooks/useRequestedFormCompletions';
import { ProfileSection } from '@/pages/Profile/types';
import { DocumentStatus } from '@/types/requestedFormCompletion';
import { Banner } from '@/ui';
import { isToday, parseIsoDate } from '@/utils/dates';

import type { Props } from './types';

const Banners = ({ person, ongoingAppointment }: Props): JSX.Element | null => {
  const {
    active_pending_discharge,
    exclude_from_analytics,
    last_intake_appointment_attended,
    last_program_adherence_appointment_attended,
    potential_duplicates,
    tags = [],
    user_terminated_account
  } = person;

  const lastAppointmentAttendanceForBanner = [
    last_program_adherence_appointment_attended,
    last_intake_appointment_attended
  ]
    .filter((appointment) => appointment?.appointment_start_at)
    .sort((left, right) => {
      return (
        new Date(right!.appointment_start_at).getTime() -
        new Date(left!.appointment_start_at).getTime()
      );
    })[0];

  let birthday: Date | undefined = undefined;
  if (person?.birthday) {
    birthday = parseIsoDate(person.birthday);
  }

  const referrer = person?.classifications.includes('referrer');

  const { data: unfinishedRequestedFormCompletions } = useRequestedFormCompletions({
    status__not: DocumentStatus.Finished,
    person_id: person.id
  });

  return (
    <>
      {lastAppointmentAttendanceForBanner ? (
        <LastAppointmentBanner lastAppointmentAttended={lastAppointmentAttendanceForBanner} />
      ) : null}
      {unfinishedRequestedFormCompletions?.length ? (
        <Banner
          label="Missing Signatures On Documents"
          detail={`(${unfinishedRequestedFormCompletions.length}) ${unfinishedRequestedFormCompletions
            ?.map((unfinishedRequestedFormCompletion) => {
              return unfinishedRequestedFormCompletion.name;
            })
            .join(', ')}`}
          color="orange"
          icon={faSignature}
        />
      ) : null}
      {exclude_from_analytics && (
        <Banner label="Excluded From Analytics / Test User" color="gray" icon={faRadiation} />
      )}
      {ongoingAppointment && (
        <a href="#ongoing-appointment-row">
          <Banner label="Ongoing Appointment" color="lime" icon={faCalendarCheck} />
        </a>
      )}
      {birthday && person.time_zone && isToday(birthday, person.time_zone) && (
        <Banner label="BIRTHDAY TODAY!" color="blue" icon={faCakeCandles} />
      )}
      {user_terminated_account && <Banner label="App Account Terminated by Member" color="red" />}
      {referrer && <Banner label="Referrer" color="indigo" icon={faArrowRightToCity} />}
      {tags.includes('Referral') && <Banner label="Referral" color="green" />}
      {tags.includes('Corporate') && <Banner label="Corporate" color="gray" />}
      {/* FIXME, needs to be aware, of referral types?! */}
      {tags.includes('Friend/Family') && <Banner label="Friend/Family" color="gray" />}
      {tags.includes('Emergency Contact') && <Banner label="Emergency Contact" color="indigo" />}
      {tags.includes('Misdial') && <Banner label="Misdial" color="yellow" />}
      {active_pending_discharge && (
        <Banner
          label="Pending Discharge"
          detail="This person has an open Discharge Needed task or their insurance has expired."
          color="yellow"
        />
      )}
      {!!potential_duplicates.length && (
        <a href={`#${ProfileSection.PERSON_DUPLICATES}`}>
          <Banner icon={faPeopleGroup} label="Potential Duplicate Members" color="amber" />
        </a>
      )}
    </>
  );
};

export default Banners;
